import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from "../ui/dialog";
import {
  Cloud, Sun, CloudRain, CloudSnow, Wind, Droplets, Clock, Thermometer,
  MapPin, Video, Upload, Plus, Loader2, ArrowLeft, Trash2, Check, CalendarDays, User, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/adventureApi";
import { authHeader, isAdmin } from "../../lib/auth";
import { typeMeta, MONTHS } from "../../lib/adventureConstants";
import { weatherIcon } from "../../lib/weather";

function VideoCard({ video, admin, onDelete }) {
  const vref = useRef(null);
  const [duration, setDuration] = useState(null);
  const [poster, setPoster] = useState(null);
  const src = `${API}/locations/videos/${video.id}`;

  const onMeta = () => {
    const v = vref.current;
    if (!v) return;
    setDuration(v.duration);
    if (!poster && isFinite(v.duration)) {
      try { v.currentTime = Math.min(1, v.duration / 2); } catch (_) {}
    }
  };
  const onSeeked = () => {
    if (poster) return;
    const v = vref.current;
    try {
      const c = document.createElement("canvas");
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      setPoster(c.toDataURL("image/jpeg", 0.6));
    } catch (_) {}
  };
  const fmt = (s) => {
    if (!s || !isFinite(s)) return null;
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  return (
    <div className="rounded-md overflow-hidden border border-sand-dark" data-testid={`video-item-${video.id}`}>
      <div className="relative">
        <video ref={vref} controls preload="metadata" poster={poster || undefined}
          onLoadedMetadata={onMeta} onSeeked={onSeeked}
          className="w-full max-h-80 bg-black" src={src} crossOrigin="anonymous" />
        {duration && (
          <span className="absolute top-2 right-2 font-mono text-[11px] bg-black/70 text-white px-1.5 py-0.5 rounded-sm pointer-events-none" data-testid={`video-duration-${video.id}`}>
            {fmt(duration)}
          </span>
        )}
      </div>
      <div className="px-4 py-3 bg-[#FAFAFA] flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {video.has_presenter_photo ? (
            <img src={`${API}/locations/videos/${video.id}/presenter`} alt={video.author}
              className="w-11 h-11 rounded-full object-cover shrink-0 border-2 border-white shadow-sm" data-testid={`presenter-photo-${video.id}`} />
          ) : (
            <div className="w-11 h-11 rounded-full bg-moss text-white flex items-center justify-center shrink-0 font-display font-bold text-sm" data-testid={`presenter-initials-${video.id}`}>
              {(video.author || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="font-semibold text-sm leading-tight">{video.title}</p>
            <p className="text-xs text-moss font-medium flex items-center gap-1"><User className="w-3 h-3" />{video.author}</p>
            {video.bio && <p className="text-xs text-ink-muted leading-snug mt-0.5">{video.bio}</p>}
          </div>
        </div>
        {admin && (
          <button data-testid={`video-delete-${video.id}`} onClick={() => onDelete(video.id)}
            className="p-2 rounded-sm text-destructive hover:bg-terracotta-light transition-colors shrink-0">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}

export default function LocationDetailModal({
  locationId, open, onClose, modules = [], onAddModule, onOpenModule, addedIds = [], serviceLevel = "comfort",
}) {
  const [data, setData] = useState(null);
  const [weather, setWeather] = useState(null);
  const [wLoading, setWLoading] = useState(false);
  const [hero, setHero] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [author, setAuthor] = useState("");
  const [title, setTitle] = useState("");
  const [bio, setBio] = useState("");
  const fileRef = useRef(null);
  const photoRef = useRef(null);
  const admin = isAdmin();

  const load = async () => {
    if (!locationId) return;
    setData(null); setWeather(null); setHero(0);
    try {
      const res = await fetch(`${API}/locations/${locationId}`);
      const json = await res.json();
      setData(json);
      const loc = json.location;
      setWLoading(true);
      try {
        const wr = await fetch(`${API}/weather?lat=${loc.lat}&lng=${loc.lng}`);
        if (wr.ok) setWeather(await wr.json());
      } catch (_) {}
      setWLoading(false);
    } catch (_) {
      toast.error("Could not load this location");
    }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, locationId]);

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("video/")) { toast.error("Please choose a video file"); return; }
    if (f.size > 80 * 1024 * 1024) { toast.error("Video too large (max 80MB)"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("author", author || "Owner");
    fd.append("title", title || "Local commentary");
    fd.append("bio", bio || "");
    const photo = photoRef.current?.files?.[0];
    if (photo) fd.append("presenter_photo", photo);
    try {
      const res = await fetch(`${API}/locations/${locationId}/videos`, {
        method: "POST", body: fd, headers: authHeader(),
      });
      if (!res.ok) throw new Error();
      toast.success("Commentary uploaded");
      setAuthor(""); setTitle(""); setBio("");
      if (photoRef.current) photoRef.current.value = "";
      await load();
    } catch (_) {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deleteVideo = async (id) => {
    try {
      const res = await fetch(`${API}/locations/videos/${id}`, { method: "DELETE", headers: authHeader() });
      if (!res.ok) throw new Error();
      toast.success("Video removed");
      await load();
    } catch (_) { toast.error("Could not delete video"); }
  };

  const loc = data?.location;
  const videos = data?.videos || [];
  const meta = loc ? typeMeta(loc.type) : null;
  const WIcon = weather ? weatherIcon(weather.code, weather.is_day) : Cloud;
  const gallery = loc?.images?.length ? loc.images : [];
  const nearby = loc ? modules.filter((m) => m.location_id === loc.id) : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 rounded-md overflow-hidden max-h-[92vh] overflow-y-auto thin-scroll" data-testid="location-detail-modal">
        <DialogTitle className="sr-only">{loc?.name || "Location"}</DialogTitle>
        <DialogDescription className="sr-only">Live conditions, description and expert commentary</DialogDescription>
        {!loc ? (
          <div className="h-64 flex items-center justify-center text-ink-secondary">
            <Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading location…
          </div>
        ) : (
          <>
            <div className="relative">
              <img src={gallery[hero]} alt={loc.name} className="w-full h-64 sm:h-72 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/75 to-transparent" />
              <button data-testid="loc-back-btn" onClick={onClose}
                className="absolute top-4 left-4 flex items-center gap-1.5 bg-white/85 backdrop-blur-md text-ink px-3 py-1.5 rounded-sm text-xs font-semibold hover:text-terracotta transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to map
              </button>
              {meta && (
                <span className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ background: meta.color }}>
                  <meta.icon className="w-3.5 h-3.5" /> {meta.label}
                </span>
              )}
              <div className="absolute bottom-4 left-5 right-5">
                <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/70 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" /> {loc.lat.toFixed(3)}, {loc.lng.toFixed(3)}
                </p>
                <h2 className="font-display font-extrabold text-3xl text-white tracking-tight">{loc.name}</h2>
              </div>
              {gallery.length > 1 && (
                <div className="absolute bottom-4 right-5 flex gap-1.5">
                  {gallery.map((g, i) => (
                    <button key={i} onClick={() => setHero(i)} data-testid={`loc-gallery-dot-${i}`}
                      className={`w-2.5 h-2.5 rounded-full ${i === hero ? "bg-white" : "bg-white/40"}`} />
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 sm:p-8 space-y-7">
              {/* Live weather */}
              <div className="rounded-md border border-sand-dark overflow-hidden" data-testid="weather-widget">
                <div className="flex items-center justify-between px-4 py-2 bg-moss text-white">
                  <span className="font-mono text-[11px] uppercase tracking-widest">Live · conditions today</span>
                  {weather?.local_time && (
                    <span className="font-mono text-[11px] flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />{weather.local_time.split("T")[1]}
                    </span>
                  )}
                </div>
                {wLoading ? (
                  <div className="p-5 flex items-center text-ink-secondary text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2 text-terracotta" /> Fetching live weather…</div>
                ) : weather ? (
                  <div className="p-5">
                    <div className="flex items-center gap-6 flex-wrap">
                      <div className="flex items-center gap-3">
                        <WIcon className="w-10 h-10 text-terracotta" />
                        <div>
                          <p className="font-mono font-extrabold text-3xl leading-none">{Math.round(weather.temperature)}°C</p>
                          <p className="text-sm text-ink-secondary">{weather.condition}</p>
                        </div>
                      </div>
                      <div className="flex gap-5 text-sm text-ink-secondary">
                        <span className="flex flex-col items-center gap-1"><Thermometer className="w-4 h-4 text-moss" /><span className="font-mono">{Math.round(weather.feels_like)}°</span><span className="text-[10px] text-ink-muted uppercase tracking-widest">Feels</span></span>
                        <span className="flex flex-col items-center gap-1"><Droplets className="w-4 h-4 text-moss" /><span className="font-mono">{weather.humidity}%</span><span className="text-[10px] text-ink-muted uppercase tracking-widest">Humid</span></span>
                        <span className="flex flex-col items-center gap-1"><Wind className="w-4 h-4 text-moss" /><span className="font-mono">{Math.round(weather.wind_speed)}</span><span className="text-[10px] text-ink-muted uppercase tracking-widest">km/h</span></span>
                      </div>
                    </div>
                    {/* 5-day forecast */}
                    {weather.forecast?.length > 0 && (
                      <div className="grid grid-cols-5 gap-2 mt-4 pt-4 border-t border-sand-dark" data-testid="weather-forecast">
                        {weather.forecast.map((d) => {
                          const FI = weatherIcon(d.code);
                          const day = new Date(d.date).toLocaleDateString("en", { weekday: "short" });
                          return (
                            <div key={d.date} className="flex flex-col items-center gap-1 text-center">
                              <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{day}</span>
                              <FI className="w-5 h-5 text-moss" />
                              <span className="font-mono text-xs"><b>{Math.round(d.max)}°</b> <span className="text-ink-muted">{Math.round(d.min)}°</span></span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="p-5 text-sm text-ink-muted">Live weather is unavailable right now.</div>
                )}
              </div>

              {/* About */}
              <div>
                <h3 className="font-mono text-xs uppercase tracking-widest text-moss mb-2">About {loc.name}</h3>
                <p className="text-ink-secondary leading-relaxed">{loc.about}</p>
              </div>

              {/* Facts */}
              {loc.facts && Object.keys(loc.facts).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(loc.facts).map(([k, v]) => (
                    <div key={k} className="border border-sand-dark rounded-sm p-3">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">{k}</p>
                      <p className="font-semibold text-sm mt-0.5">{v}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Nearby highlights */}
              {nearby.length > 0 && onAddModule && (
                <div>
                  <h3 className="font-mono text-xs uppercase tracking-widest text-moss mb-3">Experiences here</h3>
                  <div className="space-y-2">
                    {nearby.map((m) => {
                      const added = addedIds.includes(m.id);
                      return (
                        <div key={m.id} data-testid={`nearby-${m.id}`} className="flex items-center gap-3 border border-sand-dark rounded-sm p-2.5 hover:border-terracotta transition-colors">
                          <button data-testid={`nearby-details-${m.id}`} onClick={() => onOpenModule && onOpenModule(m)}
                            className="flex items-center gap-3 flex-1 min-w-0 text-left group">
                            <img src={m.image} alt="" className="w-14 h-14 rounded-sm object-cover shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm truncate group-hover:text-terracotta transition-colors">{m.name}</p>
                              <p className="font-mono text-[11px] text-ink-muted">{m.days} {m.days === 1 ? "day" : "days"} · {m.difficulty} · from ${m.prices[serviceLevel] ?? m.prices.essential}</p>
                              <span className="font-mono text-[10px] text-terracotta flex items-center gap-0.5 mt-0.5">View details <ChevronRight className="w-3 h-3" /></span>
                            </div>
                          </button>
                          <button data-testid={`nearby-add-${m.id}`} onClick={() => onAddModule(m)} disabled={added}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs font-semibold transition-colors shrink-0 ${added ? "bg-moss-light text-moss" : "bg-terracotta text-white hover:bg-terracotta-hover"}`}>
                            {added ? <><Check className="w-3.5 h-3.5" /> Added</> : <><Plus className="w-3.5 h-3.5" /> Add</>}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Expert commentary */}
              <div>
                <h3 className="font-mono text-xs uppercase tracking-widest text-moss mb-3 flex items-center gap-1.5">
                  <Video className="w-3.5 h-3.5" /> Expert commentary
                </h3>
                {videos.length === 0 ? (
                  <p className="text-sm text-ink-muted mb-4">No commentary yet for {loc.name}.</p>
                ) : (
                  <div className="space-y-4 mb-5">
                    {videos.map((v) => <VideoCard key={v.id} video={v} admin={admin} onDelete={deleteVideo} />)}
                  </div>
                )}

                {admin ? (
                  <div className="rounded-md border border-dashed border-sand-dark p-4 bg-[#FAFAFA]">
                    <p className="font-semibold text-sm mb-3 flex items-center gap-1.5"><Upload className="w-4 h-4 text-terracotta" /> Owner: add commentary</p>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <input data-testid="video-author" value={author} onChange={(e) => setAuthor(e.target.value)}
                        placeholder="Presenter name / role" className="px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm focus:outline-none focus:ring-2 focus:ring-terracotta" />
                      <input data-testid="video-title" value={title} onChange={(e) => setTitle(e.target.value)}
                        placeholder="Video title" className="px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm focus:outline-none focus:ring-2 focus:ring-terracotta" />
                    </div>
                    <input data-testid="video-bio" value={bio} onChange={(e) => setBio(e.target.value)}
                      placeholder="Short presenter bio (optional)" className="w-full mb-3 px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm focus:outline-none focus:ring-2 focus:ring-terracotta" />
                    <div className="flex items-center gap-2 mb-3">
                      <label className="font-mono text-[11px] uppercase tracking-widest text-ink-muted">Presenter photo</label>
                      <input ref={photoRef} type="file" accept="image/*" data-testid="presenter-photo-input"
                        className="text-xs file:mr-2 file:px-2 file:py-1 file:rounded-sm file:border-0 file:bg-moss-light file:text-moss file:font-semibold" />
                    </div>
                    <input ref={fileRef} type="file" accept="video/*" onChange={handleUpload} className="hidden" data-testid="video-file-input" />
                    <button data-testid="video-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
                      className="flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white px-4 py-2.5 rounded-sm text-sm font-semibold tracking-wide transition-colors disabled:opacity-50">
                      {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Plus className="w-4 h-4" /> Choose video (max 80MB)</>}
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-ink-muted italic">Commentary videos are curated by the tour owner.</p>
                )}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
